import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Flame, Clock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function useTempleStatus() {
  const [status, setStatus] = useState({ open: false, label: "" });
  useEffect(() => {
    const compute = () => {
      const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
      const mins = now.getHours() * 60 + now.getMinutes();
      const isSaturday = now.getDay() === 6;
      const MORNING = [8 * 60 + 30, 11 * 60 + 30];
      const EVENING = [17 * 60, 20 * 60 + 30];
      if (isSaturday && mins >= MORNING[0] && mins <= MORNING[1]) {
        setStatus({ open: true, label: "Open now — Morning Darshan until 11:30 AM CT" });
      } else if (isSaturday && mins >= EVENING[0] && mins <= EVENING[1]) {
        setStatus({ open: true, label: "Open now — Evening Aarti until 8:30 PM CT" });
      } else {
        setStatus({ open: false, label: "Open Saturdays · 8:30–11:30 AM & 5:00–8:30 PM CT" });
      }
    };
    compute();
    const t = setInterval(compute, 60000);
    return () => clearInterval(t);
  }, []);
  return status;
}

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-1 ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function HeroSection() {
  const status = useTempleStatus();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-stone-950">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgY, scale: bgScale }}>
        <motion.img
          aria-hidden="true"
          src="/images/hanuman-sunset-1.webp"
          alt=""
          initial={{ scale: 1.3, filter: "brightness(0.45) blur(18px)" }}
          animate={{ scale: 1.18, filter: "brightness(0.6) blur(18px)" }}
          transition={{ duration: 2.4, ease: EASE }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <motion.img
          src="/images/hanuman-sunset-1.webp"
          alt="Granite murti of Lord Hanuman at sunset at the Denison temple — shown in full"
          data-testid="hero-hanuman-image"
          initial={{ scale: 1.15, filter: "brightness(0.6)" }}
          animate={{ scale: 1, filter: "brightness(1)" }}
          transition={{ duration: 2.4, ease: EASE }}
          className="relative w-full h-full object-contain object-center"
        />
      </motion.div>
      <div className="hero-vignette absolute inset-0" />

      <motion.div style={{ opacity: contentFade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32 pt-44 w-full">
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.15, ease: EASE }}
          className="origin-left flex items-center gap-4 mb-8"
          data-testid="hero-mantra"
        >
          <span className="h-px w-10 sm:w-16 bg-amber-400/70" />
          <span className="font-display text-amber-300 tracking-[0.4em] text-xs sm:text-sm">॥ जय श्री राम · जय हनुमान ॥</span>
          <span className="h-px w-10 sm:w-16 bg-amber-400/70" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
        >
          <div data-testid="temple-hours-badge"
            className="inline-flex items-center gap-2 rounded-full border border-amber-200/30 bg-stone-950/50 backdrop-blur-md px-4 py-1.5 mb-7">
            <span className={`h-2 w-2 rounded-full ${status.open ? "bg-green-400" : "bg-amber-400"} animate-pulse`} />
            <span className="text-xs font-medium tracking-wide text-amber-100/90 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {status.label}
            </span>
          </div>
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="font-display font-bold tracking-tight text-amber-50 max-w-4xl"
        >
          <MaskedLine delay={0.5} className="text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">Neem Karoli Baba Mandir</MaskedLine>
          <MaskedLine delay={0.68} className="text-2xl sm:text-3xl lg:text-4xl leading-[1.2] text-amber-400 mt-2">at Osho Kaivalya Dham</MaskedLine>
          <MaskedLine delay={0.84} className="text-base sm:text-lg lg:text-xl leading-[1.2] text-amber-200/90 tracking-[0.3em] mt-1.5">SPIRITUAL VILLAGE</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.92, ease: EASE }}
          className="mt-5 flex items-center gap-2 text-amber-100/80 text-sm sm:text-base tracking-wide"
          data-testid="hero-location"
        >
          <MapPin className="h-4 w-4 text-amber-400" /> 1642 FM 1310 · Denison, Texas — the Neem Karoli Baba temple serving Dallas–Fort Worth (DFW) & Texoma
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.05, ease: EASE }}
          className="mt-6 font-quote text-xl sm:text-2xl italic text-amber-100/95 max-w-2xl leading-relaxed"
          data-testid="hero-motto"
        >
          "Love everyone. Serve everyone. Feed everyone. Remember God."
          <span className="block mt-1 text-sm not-italic font-sans tracking-[0.25em] uppercase text-amber-300/80">— Maharaj-ji · Sub Ek, All is One</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.2, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a href="#visit" data-testid="hero-cta-visit"
            className="inline-flex items-center gap-2 rounded-full border border-amber-200/40 bg-amber-50/10 backdrop-blur-md px-7 py-3 text-sm font-semibold text-amber-50 hover:bg-amber-50/20 hover:-translate-y-0.5 transition-all duration-300">
            <MapPin className="h-4 w-4" /> Plan Your Visit
          </a>
          <a href="#donate" data-testid="hero-cta-donate"
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-7 py-3 text-sm font-semibold text-white shadow-xl shadow-orange-950/40 hover:shadow-orange-900/50 hover:-translate-y-0.5 transition-all duration-300">
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out" />
            <Flame className="flame h-4 w-4" /> Help Build the New Temple
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-amber-200/60">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="block h-8 w-px bg-gradient-to-b from-amber-300/80 to-transparent"
        />
      </motion.div>
    </section>
  );
}
